import { useEffect, useRef } from "react";

import { echarts, PALETTE } from "../charts/echarts";
import type { SectorHeat } from "../api/types";

function colorForChange(pct: number | null): string {
  if (pct === null || pct === undefined) return PALETTE.muted;
  if (pct >= 2) return "#10b981";    // emerald-500
  if (pct >= 0.5) return "#34d399";  // emerald-400
  if (pct > -0.5) return "#64748b";  // slate-500
  if (pct > -2) return "#f87171";    // rose-400
  return "#ef4444";                  // rose-500
}

export default function SectorTreemap({ data }: { data: SectorHeat[] }) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    if (!data.length) return;
    const inst = echarts.init(ref.current);
    // Treemap data: each leaf = one sector. Size by mention_count + 1
    // (so an all-zero day still renders something proportional). Color by
    // avg_day_change_pct.
    const nodes = data.map((s) => ({
      name: s.sector,
      value: Math.max(1, s.mention_count) + Math.abs(s.avg_day_change_pct ?? 0) * 5,
      itemStyle: { color: colorForChange(s.avg_day_change_pct) },
      sectorMeta: s,
    }));
    inst.setOption({
      tooltip: {
        formatter: (p: any) => {
          const m = p.data?.sectorMeta as SectorHeat | undefined;
          if (!m) return p.name;
          const dc = m.avg_day_change_pct;
          const dcStr = dc === null ? "—" : `${dc >= 0 ? "+" : ""}${dc.toFixed(2)}%`;
          const sc = m.avg_composite_score === null ? "—" : m.avg_composite_score.toFixed(0);
          const tt = (m.top_tickers || []).slice(0, 3).join(", ");
          return `<b>${m.sector}</b><br/>day Δ: ${dcStr}<br/>score: ${sc}<br/>top: ${tt}`;
        },
        backgroundColor: "rgba(15,23,42,0.95)",
        borderColor: PALETTE.grid,
        textStyle: { color: PALETTE.text, fontSize: 11 },
      },
      series: [{
        type: "treemap",
        roam: false,
        nodeClick: false,
        breadcrumb: { show: false },
        label: {
          show: true,
          color: "#0f172a",
          fontWeight: 600,
          fontSize: 12,
          formatter: (p: any) => {
            const m = p.data?.sectorMeta as SectorHeat | undefined;
            if (!m) return p.name;
            const dc = m.avg_day_change_pct;
            return `${m.sector}\n${dc === null ? "" : (dc >= 0 ? "+" : "") + dc.toFixed(1) + "%"}`;
          },
        },
        upperLabel: { show: false },
        itemStyle: {
          borderColor: PALETTE.bg,
          borderWidth: 2,
          gapWidth: 2,
        },
        data: nodes,
      }],
      backgroundColor: "transparent",
    });
    const onResize = () => inst.resize();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      inst.dispose();
    };
  }, [data]);

  return <div ref={ref} className="h-72 w-full" />;
}
